import React, { useState } from "react";
import { ArrowRight, Check } from "lucide-react";

const SECTOR_OPTIONS = [
  { id: "School", label: "School / K-12" },
  { id: "Veteran", label: "Veteran community" },
  { id: "Rural", label: "Rural facility" },
  { id: "BehavioralHealth", label: "Behavioral health" },
  { id: "Youth", label: "Youth residential" },
  { id: "Workplace", label: "Workplace / Other" },
];

const SIZE_OPTIONS = [
  { id: "Small", label: "Small", note: "< 50 people on-site" },
  { id: "Medium", label: "Medium", note: "50 – 250 people" },
  { id: "Large", label: "Large", note: "250 – 1,000 people" },
  { id: "Campus", label: "Campus", note: "1,000+ / multi-building" },
];

const WINDOW_OPTIONS = [
  { id: "Next4Weeks", label: "Within 4 weeks" },
  { id: "Next3Months", label: "Within 3 months" },
  { id: "Exploring", label: "Just exploring" },
];

export default function ScheduleAssessment({ onComplete }) {
  const [step, setStep] = useState(0);
  const [picks, setPicks] = useState({ sector: null, size: null, window: null });

  const choose = (key, value) => {
    const next = { ...picks, [key]: value };
    setPicks(next);
    if (step < 2) {
      setStep(step + 1);
    } else {
      onComplete?.(next);
    }
  };

  return (
    <section
      data-testid="schedule-assessment"
      className="bg-white border border-slate-200"
    >
      <div className="p-6 md:p-10">
        <div className="flex items-start justify-between gap-6 mb-6">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-600">
              Schedule a Site Assessment · 3 quick selections
            </div>
            <h2 className="mt-3 font-display font-black text-2xl md:text-4xl tracking-tight text-slate-900">
              Tell us about your environment.
            </h2>
            <p className="mt-2 text-slate-600 max-w-2xl">
              The Coalition reviews every inquiry in deployment order. Three taps and
              we'll pre-fill the rest of the form for you.
            </p>
          </div>
          <div className="hidden md:flex flex-col items-end gap-1 shrink-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
              Step {step + 1} of 3
            </div>
            <div className="flex gap-1.5">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className={`h-1.5 w-8 ${i <= step ? "bg-amber-600" : "bg-slate-200"} transition-colors`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Step 0: Sector */}
        {step === 0 && (
          <div data-testid="assessment-step-sector">
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-4">
              01 · Sector
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {SECTOR_OPTIONS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`assessment-sector-${s.id}`}
                  onClick={() => choose("sector", s)}
                  className="text-left border border-slate-200 hover:border-amber-600 hover:bg-amber-50/40 px-5 py-4 transition-colors"
                >
                  <div className="font-display font-bold text-slate-900">{s.label}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Size */}
        {step === 1 && (
          <div data-testid="assessment-step-size">
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-4">
              02 · Environment size
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {SIZE_OPTIONS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`assessment-size-${s.id}`}
                  onClick={() => choose("size", s)}
                  className="text-left border border-slate-200 hover:border-amber-600 hover:bg-amber-50/40 px-5 py-4 transition-colors"
                >
                  <div className="font-display font-bold text-slate-900">{s.label}</div>
                  <div className="text-xs text-slate-500 mt-1">{s.note}</div>
                </button>
              ))}
            </div>
            <button
              type="button"
              data-testid="assessment-back"
              onClick={() => setStep(0)}
              className="mt-6 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500 hover:text-slate-900"
            >
              ← back
            </button>
          </div>
        )}

        {/* Step 2: Window */}
        {step === 2 && (
          <div data-testid="assessment-step-window">
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-4">
              03 · Preferred timeframe
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {WINDOW_OPTIONS.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  data-testid={`assessment-window-${w.id}`}
                  onClick={() => choose("window", w)}
                  className="group text-left border border-slate-200 hover:border-amber-600 hover:bg-amber-50/40 px-5 py-4 transition-colors flex items-center justify-between gap-3"
                >
                  <span className="font-display font-bold text-slate-900">{w.label}</span>
                  <ArrowRight size={16} className="text-slate-400 group-hover:text-amber-600 group-hover:translate-x-1 transition-all" />
                </button>
              ))}
            </div>
            <button
              type="button"
              data-testid="assessment-back-2"
              onClick={() => setStep(1)}
              className="mt-6 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500 hover:text-slate-900"
            >
              ← back
            </button>
          </div>
        )}

        {/* Selected chips */}
        {(picks.sector || picks.size) && (
          <div className="mt-8 pt-6 border-t border-slate-200 flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 mr-2">
              Your selections
            </span>
            {picks.sector && (
              <span data-testid="chip-sector" className="inline-flex items-center gap-2 bg-slate-900 text-white text-xs px-3 py-1.5">
                <Check size={12} /> {picks.sector.label}
              </span>
            )}
            {picks.size && (
              <span data-testid="chip-size" className="inline-flex items-center gap-2 bg-slate-900 text-white text-xs px-3 py-1.5">
                <Check size={12} /> {picks.size.label}
              </span>
            )}
            {picks.window && (
              <span data-testid="chip-window" className="inline-flex items-center gap-2 bg-slate-900 text-white text-xs px-3 py-1.5">
                <Check size={12} /> {picks.window.label}
              </span>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
